import { Heading, VerticalSpace, Tabs } from '@elhub/ds-components'
import { LabelMessages } from 'locales/messages/label'
import tokens from '@elhub/ds-tokens/dist/tokens.json'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CodeSnippet from '../CodeSnippet'

interface DatasetExampleProps {
  entity: string
}

type RequestType = {
  all: string,
  one: string,
  export: string
}

type ExampleUrls = {
  consumption: RequestType
}

const DatasetExamples: React.FC<DatasetExampleProps> = ({ entity }) => {
  const [tabValue, setTabValue] = useState<string>('all')
  const { t } = useTranslation()

  return (
    <>
      {getEntityExamples(entity, t, tabValue, setTabValue)}
      <VerticalSpace />
    </>
  )
}

const getEntityExamples = (entity: string, t: any, tabValue: string, setTabValue: (value: string) => void) => {
  let defaultId = ''
  switch (entity) {
    case 'municipalities':
      defaultId = '/0301'
      break
    case 'grid-areas':
      defaultId = '/50Y2VA2X6F00RRCT'
      break
    case 'price-areas':
      defaultId = '/NO1'
      break
    case 'metering-points':
      defaultId = '/{your-metering-point-id}'
      break
    default:
      defaultId = ''
  }

  const examples: Record<string, ExampleUrls> = {
    municipalities: {
      consumption: {
        all: 'https://api.elhub.no/energy-data/v0/municipalities?dataset=CONSUMPTION_PER_GROUP_MUNICIPALITY_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        one: 'https://api.elhub.no/energy-data/v0/municipalities' + defaultId + '?dataset=CONSUMPTION_PER_GROUP_MUNICIPALITY_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        export: 'https://api.elhub.no/energy-data/v0/municipalities.csv?dataset=CONSUMPTION_PER_GROUP_MUNICIPALITY_HOUR'
      }
    },
    'grid-areas': {
      consumption: {
        all: 'https://api.elhub.no/energy-data/v0/grid-areas?dataset=CONSUMPTION_PER_GROUP_MGA_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        one: 'https://api.elhub.no/energy-data/v0/grid-areas' + defaultId + '?dataset=CONSUMPTION_PER_GROUP_MGA_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        export: 'https://api.elhub.no/energy-data/v0/grid-areas.csv?dataset=CONSUMPTION_PER_GROUP_MGA_HOUR'
      }
    },
    'price-areas': {
      consumption: {
        all: 'https://api.elhub.no/energy-data/v0/price-areas?dataset=CONSUMPTION_PER_GROUP_MBA_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        one: 'https://api.elhub.no/energy-data/v0/price-areas' + defaultId + '?dataset=CONSUMPTION_PER_GROUP_MBA_HOUR&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00&consumptionGroup=private',
        export: 'https://api.elhub.no/energy-data/v0/price-areas.csv?dataset=CONSUMPTION_PER_GROUP_MBA_HOUR'
      }
    },
    'metering-points': {
      consumption: {
        all: 'https://api.elhub.no/energy-data/v0/metering-points?dataset=METERING_VALUES_HOUR_RESTRICTED&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00',
        one: 'https://api.elhub.no/energy-data/v0/metering-points' + defaultId + '?dataset=METERING_VALUES_HOUR_RESTRICTED&startDate=2023-05-03T20:00:00%2B02:00&endDate=2023-05-04T00:00:00%2B02:00',
        export: 'https://api.elhub.no/energy-data/v0/metering-points.csv?dataset=METERING_VALUES_HOUR_RESTRICTED'
      }
    }
  }

  const entityExamples = examples[entity]

  const generateCodeSnippet = (url: string, entity: string) => {
    const headers: { Authorization: string; 'X-Elhub-GLN': string } | {} = entity === 'metering-points'
      ? {
          Authorization: 'Bearer {MASKINPORTEN_ACCESS_TOKEN}',
          'X-Elhub-GLN': '{YOUR_GLN_VALUE}'
        }
      : {}

    return <CodeSnippet url={url} headers={headers} disableBackground />
  }

  return (
    <>
      {entityExamples &&
        Object.entries(entityExamples).map(([key, url]) => (
          <React.Fragment key={key}>
            <Heading size='xsmall' level='5'>
              <b>Sample requests:</b>
            </Heading>
            <VerticalSpace size='1' />
            <Tabs value={tabValue} onChange={setTabValue}>
              <Tabs.List>
                <Tabs.Tab value='all' label={t(LabelMessages.ALL as any)} />
                <Tabs.Tab value='one' label={t(LabelMessages.ONE as any)} />
                {entity !== 'metering-points' && <Tabs.Tab value='export' label={t(LabelMessages.EXPORT as any)} />}
              </Tabs.List>
              <Tabs.Panel value='all' style={{ backgroundColor: tokens['eds-color-grey-lighter90'] }}>
                {generateCodeSnippet(url.all, entity)}
                <VerticalSpace size='1' />
              </Tabs.Panel>
              <Tabs.Panel value='one' style={{ backgroundColor: tokens['eds-color-grey-lighter90'] }}>
                {generateCodeSnippet(url.one, entity)}
                <VerticalSpace size='1' />
              </Tabs.Panel>
              {entity !== 'metering-points' &&
                <Tabs.Panel value='export' style={{ backgroundColor: tokens['eds-color-grey-lighter90'] }}>
                  {generateCodeSnippet(url.export, entity)}
                  <VerticalSpace size='1' />
                </Tabs.Panel>}
            </Tabs>
            <VerticalSpace />
          </React.Fragment>
        ))}
    </>
  )
}

export default DatasetExamples
